import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { IContactType, ICountry, ICommonDataSource, INationality, IQualification, ISource, ISourceType, IUniversity } from "../interfaces";
import { BehaviorSubject, of, tap } from "rxjs";
import { CandidateService } from "../services/candidate.service";
import { UtilCommon } from "app/shared/common/utils/UtilCommon";
import { AppStateConstant } from "app/shared/common/constants/AppState.constant";
import { Router } from "@angular/router";

@Injectable({ providedIn: "root" })
export class CandidateStore {
  readonly #candidateService: CandidateService = inject(CandidateService);

  readonly #nationality: BehaviorSubject<INationality[]> = new BehaviorSubject<INationality[]>([]);
  readonly #sourceType: BehaviorSubject<ISourceType[]> = new BehaviorSubject<ISourceType[]>([]);
  readonly #sources: BehaviorSubject<ISource[]> = new BehaviorSubject<ISource[]>([]);
  readonly #allSourceTypes: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  readonly #channels: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #contactType: BehaviorSubject<IContactType[]> = new BehaviorSubject<IContactType[]>([]);
  readonly #countries: BehaviorSubject<ICountry[]> = new BehaviorSubject<ICountry[]>([]);
  readonly #issuePlaces: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #universities: BehaviorSubject<IUniversity[]> = new BehaviorSubject<IUniversity[]>([]);
  readonly #qualifications: BehaviorSubject<IQualification[]> = new BehaviorSubject<IQualification[]>([]);
  readonly #qualificationTypes: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #majors: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #faculties: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #qualificationRates: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #industries: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #jobTypes: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #jobTracks: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #jobFamilies: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #jobGroups: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #subGroups: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #documentTypes: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #employees: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #skills: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #attachmentTypes: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #marriedStatuses: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #jobTitles: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #getRelations: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #summaryType: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #genders = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #employeeLevel: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #offices: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);
  readonly #city: BehaviorSubject<ICommonDataSource[]> = new BehaviorSubject<ICommonDataSource[]>([]);

  readonly nationality$ = this.#nationality.asObservable();
  readonly sourceType$ = this.#sourceType.asObservable();
  readonly allSourceTypes$ = this.#allSourceTypes.asObservable();
  readonly sources$ = this.#sources.asObservable();
  readonly channels$ = this.#channels.asObservable();
  readonly sourceFilterd$ = new BehaviorSubject<ISource[]>([]);
  readonly contactType$ = this.#contactType.asObservable();
  readonly countries$ = this.#countries.asObservable();
  readonly qualificationTypes$ = this.#qualificationTypes.asObservable();
  readonly universities$ = this.#universities.asObservable();
  readonly qualifications$ = this.#qualifications.asObservable();
  readonly majors$ = this.#majors.asObservable();
  readonly faculties$ = this.#faculties.asObservable();
  readonly facultiesFiltered$ = new BehaviorSubject<ICommonDataSource[]>(this.#faculties.value);;
  readonly qualificationRates$ = this.#qualificationRates.asObservable();
  readonly industries$ = this.#industries.asObservable();
  readonly jobTypes$ = this.#jobTypes.asObservable();
  readonly jobTracks$ = this.#jobTracks.asObservable();
  readonly jobFamilies$ = this.#jobFamilies.asObservable();
  readonly jobGroups$ = this.#jobGroups.asObservable();
  readonly subGroups$ = this.#subGroups.asObservable();
  readonly documentTypes$ = this.#documentTypes.asObservable();
  readonly issuePlaces$ = this.#issuePlaces.asObservable();
  readonly city$ = this.#city.asObservable();
  readonly employees$ = this.#employees.asObservable();
  readonly skills$ = this.#skills.asObservable();
  readonly attachmentTypes$ = this.#attachmentTypes.asObservable();
  readonly marriedStatuses$ = this.#marriedStatuses.asObservable();
  readonly jobTitles$ = this.#jobTitles.asObservable();
  readonly offices$ = this.#offices.asObservable();
  readonly relations$ = this.#getRelations.asObservable();
  readonly summaryType$ = this.#summaryType.asObservable();
  readonly genders$ = this.#genders.asObservable();
  readonly employeeLevel$ = this.#employeeLevel.asObservable();

  readonly isStable: WritableSignal<boolean> = signal(false);
  requisitions$: any;

  preloadData$() {
    if (this.isStable()) {
      return of(true);
    }

    let hasuraToken = localStorage.getItem(AppStateConstant.HasuraToken);
    let serverToken = localStorage.getItem(AppStateConstant.ServerToken);
    // Check if both tokens exist before calling getPostResolver$
    if (!hasuraToken || !serverToken) {
      console.warn('Missing required tokens (hasuraToken or serverToken)');
      // Navigate to sign-in page
      const router = inject(Router);
      router.navigate(['/sign-in']);
      return of(false); // or return of(true) if you want to continue anyway
    }

    return of(true).pipe(
      tap(() => {
        this.#loadDependencies()
      })
    );
  }

  public getCandidateResolver$() {
    return this.#candidateService.getCandidateResolver()
      .pipe(tap((data) => {
        UtilCommon.sortArray(data?.gender);
        UtilCommon.sortArray(data?.allSourceType);
        UtilCommon.sortArray(data?.sourceType);
        UtilCommon.sortArray(data?.source);
        UtilCommon.sortArray(data?.contactType);
        UtilCommon.sortArray(data?.country);
        UtilCommon.sortArray(data?.qualificationType);
        UtilCommon.sortArray(data?.university);
        UtilCommon.sortArray(data?.qualification);
        UtilCommon.sortArray(data?.major);
        UtilCommon.sortArray(data?.faculty);
        UtilCommon.sortArray(data?.qualificationRate);
        UtilCommon.sortArray(data?.industry);
        UtilCommon.sortArray(data?.nationality);
        UtilCommon.sortArray(data?.legaldocType);
        this.#genders.next(data?.gender || []);
        this.#allSourceTypes.next(data?.allSourceType || []);
        this.#sourceType.next(data?.sourceType || []);
        this.#sources.next(data?.source || []);
        this.#contactType.next(data?.contactType || []);
        this.#countries.next(data?.country || []);
        this.#qualificationTypes.next(data?.qualificationType || []);
        this.#universities.next(data?.university || []);
        this.#qualifications.next(data?.qualification || []);
        this.#majors.next(data?.major || []);
        this.#faculties.next(data?.faculty || []);
        this.#qualificationRates.next(data?.qualificationRate || []);
        this.#industries.next(data?.industry || []);
        this.#nationality.next(data?.nationality || []);
        this.#documentTypes.next(data?.legaldocType || []);
      }))
  }

  public getCandidateExtResolver$() {
    return this.#candidateService.getCandidateExtResolver()
      .pipe(tap((data) => {
        UtilCommon.sortArray(data?.jobType);
        UtilCommon.sortArray(data?.jobTrack);
        UtilCommon.sortArray(data?.jobFamily);
        UtilCommon.sortArray(data?.jobGroup);
        UtilCommon.sortArray(data?.subGroup);
        UtilCommon.sortArray(data?.issuePlace);
        UtilCommon.sortArray(data?.attachmentType);
        UtilCommon.sortArray(data?.maritalStatus);
        // UtilCommon.sortArray(data?.jobTitle);
        UtilCommon.sortArray(data?.office);
        UtilCommon.sortArray(data?.relation);
        UtilCommon.sortArray(data?.summaryType);
        UtilCommon.sortArray(data?.channel);
        UtilCommon.sortArray(data?.employeeLevel);
        UtilCommon.sortArray(data?.city);
        this.#jobTypes.next(data?.jobType || []);
        this.#jobTracks.next(data?.jobTrack || []);
        this.#jobFamilies.next(data?.jobFamily || []);
        this.#jobGroups.next(data?.jobGroup || []);
        this.#subGroups.next(data?.subGroup || []);
        this.#issuePlaces.next(data?.issuePlace || []);
        this.#attachmentTypes.next(data?.attachmentType || []);
        this.#marriedStatuses.next(data?.maritalStatus || []);
        // this.#jobTitles.next(data?.jobTitle || []);
        this.#offices.next(data?.office || []);
        this.#getRelations.next(data?.relation || []);
        this.#summaryType.next(data?.summaryType || []);
        this.#channels.next(data?.channel || []);
        this.#employeeLevel.next(data?.employeeLevel || []);
        this.#city.next(data?.city || []);
      }))
  }

  #loadDependencies() {
    // this.getGenders$().subscribe();
    // this.getAllSourceTypes$().subscribe();
    // this.getSourceType$().subscribe();
    // this.getAllSources$().subscribe();
    // this.getContactType$().subscribe();
    // this.getCountries$().subscribe();
    // this.getQualificaitonTypes$().subscribe();
    // this.getUniversities$().subscribe();
    // this.getQualificationDeg$().subscribe();
    // this.getMajors$().subscribe();
    // this.getFaculties$().subscribe();
    // this.getQualificationRates$().subscribe();
    // this.getIndustries$().subscribe();
    // this.getNationality$().subscribe();
    // this.getLegalDocTypes$().subscribe();
    this.getCandidateResolver$().subscribe();

    // this.getJobTypes$().subscribe();
    // this.getJobTracks$().subscribe();
    // this.getJobFamilies$().subscribe();
    // this.getJobGroups$().subscribe();
    // this.getSubGroups$().subscribe();
    // this.getIssuePlaces$().subscribe();
    // this.getAttachmentTypes$().subscribe();
    // this.getMarriedStatuses$().subscribe();
    this.getJobTitles$().subscribe();
    // this.getOffices$().subscribe();
    // this.getRelations$().subscribe();
    // this.getSummaryType$().subscribe();
    // this.getAllChannels$().subscribe();
    // this.getEmployeeLevel$().subscribe();
    this.getCandidateExtResolver$().subscribe();

    this.getEmployees$().subscribe();
    this.getSkills$().subscribe();
    this.isStable.set(true);
  }

  public getGenders() {
    UtilCommon.sortArray(this.#genders.value);
    return this.#genders.value;
  }

  public getSummaryType() {
    UtilCommon.sortArray(this.#summaryType.value);
    return this.#summaryType.value;
  }

  public getAttachmentTypesValue() {
    UtilCommon.sortArray(this.#attachmentTypes.value);
    return this.#attachmentTypes.value;
  }

  public getMarialStatuses() {
    UtilCommon.sortArray(this.#marriedStatuses.value);
    return this.#marriedStatuses.value;
  }

  public getSourceType() {
    UtilCommon.sortArray(this.#sourceType.value);
    return this.#sourceType.value;
  }

  public getAllSourceTypes() {
    UtilCommon.sortArray(this.#allSourceTypes.value);
    return this.#allSourceTypes.value;
  }

  public getRecruitSource() {
    UtilCommon.sortArray(this.#sources.value);
    return this.#sources.value;
  }

  public getChannels() {
    UtilCommon.sortArray(this.#channels.value);
    return this.#channels.value;
  }

  public getSchools() {
    UtilCommon.sortArray(this.#universities.value);
    return this.#universities.value;
  }

  public getCountries() {
    UtilCommon.sortArray(this.#countries.value);
    return this.#countries.value;
  }

  public getQualificationTypes() {
    UtilCommon.sortArray(this.#qualificationTypes.value);
    return this.#qualificationTypes.value;
  }

  public getQualifications() {
    UtilCommon.sortArray(this.#qualifications.value);
    return this.#qualifications.value;
  }

  public getFaculties() {
    UtilCommon.sortArray(this.#faculties.value);
    return this.#faculties.value;
  }

  public getMajors() {
    UtilCommon.sortArray(this.#majors.value);
    return this.#majors.value;
  }

  public getQualificationRates() {
    UtilCommon.sortArray(this.#qualificationRates.value);
    return this.#qualificationRates.value;
  }

  public getIndustries() {
    UtilCommon.sortArray(this.#industries.value);
    return this.#industries.value;
  }

  public getJobTypes() {
    UtilCommon.sortArray(this.#jobTypes.value);
    return this.#jobTypes.value;
  }

  public getJobTracks() {
    UtilCommon.sortArray(this.#jobTracks.value);
    return this.#jobTracks.value;
  }

  public getJobFamilies() {
    UtilCommon.sortArray(this.#jobFamilies.value);
    return this.#jobFamilies.value;
  }

  public getJobGroups() {
    UtilCommon.sortArray(this.#jobGroups.value);
    return this.#jobGroups.value;
  }

  public getSubGroups() {
    UtilCommon.sortArray(this.#subGroups.value);
    return this.#subGroups.value;
  }

  public getJobTitles() {
    UtilCommon.sortArray(this.#jobTitles.value);
    return this.#jobTitles.value;
  }

  public getOffices() {
    UtilCommon.sortArray(this.#offices.value);
    return this.#offices.value;
  }

  public getSkills() {
    UtilCommon.sortArray(this.#skills.value);
    return this.#skills.value;
  }

  public getLegalDocTypes() {
    UtilCommon.sortArray(this.#documentTypes.value);
    return this.#documentTypes.value;
  }

  public getIssuePlaces() {
    UtilCommon.sortArray(this.#issuePlaces.value);
    return this.#issuePlaces.value;
  }

  public getCity() {
    UtilCommon.sortArray(this.#city.value);
    return this.#city.value;
  }

  public getContactTypes() {
    UtilCommon.sortArray(this.#contactType.value);
    return this.#contactType.value;
  }

  public getRelations() {
    UtilCommon.sortArray(this.#getRelations.value);
    return this.#getRelations.value;
  }

  public getEmployees() {
    UtilCommon.sortArray(this.#employees.value);
    return this.#employees.value;
  }

  public getEmployeeLevel() {
    UtilCommon.sortArray(this.#employeeLevel.value);
    return this.#employeeLevel.value;
  }

  public getNationality$() {
    if (this.#nationality.value.length === 0) {
      return this.#candidateService.getNationalities()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#nationality.next(data); }))
    }
    return this.#nationality.asObservable();
  }

  public getSourceType$() {
    if (this.#sourceType.value.length === 0) {
      return this.#candidateService.getSourceType()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#sourceType.next(data); }))

    }
    return this.#sourceType.asObservable();
  }

  public getAllSourceTypes$() {
    if (this.#allSourceTypes.value.length === 0) {
      return this.#candidateService.getAllSourceTypes()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#allSourceTypes.next(data); }))
    }
    return this.#allSourceTypes.asObservable();
  }

  public getAllSources$() {
    if (this.#sources.value.length === 0) {
      return this.#candidateService.getAllSources()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#sources.next(data); }))
    }
    return this.#sources.asObservable();
  }

  public getAllChannels$() {
    if (this.#channels.value.length === 0) {
      return this.#candidateService.getAllChannels()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#channels.next(data); }))
    }
    return this.#channels.asObservable();
  }

  public getSourcesById$(sourceTypeId: number) {
    this.sourceFilterd$.next(
      this.#sources.value.filter((source) => source?.channelTypeId === sourceTypeId)
    )
  }

  public getContactType$() {
    if (this.#contactType.value.length === 0) {
      return this.#candidateService.getContactType()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#contactType.next(data); }))
    }
    return this.#contactType.asObservable();
  }

  public getCountries$() {
    if (this.#countries.value.length === 0) {
      return this.#candidateService.getCountries()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#countries.next(data); }))
    }
    return this.#countries.asObservable();
  }

  public getQualificaitonTypes$() {
    if (this.#qualificationTypes.value.length === 0) {
      return this.#candidateService.getQualificationTypes()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#qualificationTypes.next(data); }))
    }
    return this.#qualificationTypes.asObservable();
  }

  public getQualificationDeg$() {
    if (this.#qualifications.value.length === 0) {
      return this.#candidateService.getQualificationDeg()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#qualifications.next(data); }))
    }
    return this.#qualifications.asObservable();
  }

  public getUniversities$() {
    if (this.#universities.value.length === 0) {
      return this.#candidateService.getUniversities()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#universities.next(data); }))
    }
    return this.#universities.asObservable();
  }

  public getMajors$() {
    if (this.#majors.value.length === 0) {
      return this.#candidateService.getMajors()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#majors.next(data); }))
    }
    return this.#majors.asObservable();
  }

  public getFaculties$() {
    if (this.#faculties.value.length === 0) {
      return this.#candidateService.getFaculties()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#faculties.next(data); }))
    }
    return this.#faculties.asObservable();
  }

  public getQualificationRates$() {
    if (this.#qualificationRates.value.length === 0) {
      return this.#candidateService.getQualificationRates()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#qualificationRates.next(data); }))
    }
    return this.#qualificationRates.asObservable();
  }

  public getIndustries$() {
    if (this.#industries.value.length === 0) {
      return this.#candidateService.getIndustries()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#industries.next(data); }))
    }
    return this.#industries.asObservable();
  }

  public getJobTypes$() {
    if (this.#jobTypes.value.length === 0) {
      return this.#candidateService.getJobTypes()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#jobTypes.next(data); }))
    }
    return this.#jobTypes.asObservable();
  }

  public getJobTracks$() {
    if (this.#jobTracks.value.length === 0) {
      return this.#candidateService.getJobTracks()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#jobTracks.next(data); }))
    }
    return this.#jobTracks.asObservable();
  }

  public getJobFamilies$() {
    if (this.#jobFamilies.value.length === 0) {
      return this.#candidateService.getJobFamilies()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#jobFamilies.next(data); }))
    }
    return this.#jobFamilies.asObservable();
  }

  public getJobGroups$() {
    if (this.#jobGroups.value.length === 0) {
      return this.#candidateService.getJobGroups()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#jobGroups.next(data); }))
    }
    return this.#jobGroups.asObservable();
  }

  public getSubGroups$() {
    if (this.#subGroups.value.length === 0) {
      return this.#candidateService.getSubGroups()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#subGroups.next(data); }))
    }
    return this.#subGroups.asObservable();
  }

  public getJobTitles$() {
    if (this.#jobTitles.value.length === 0) {
      return this.#candidateService.getJobTitles()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#jobTitles.next(data); }))
    }
    return this.#jobTitles.asObservable();
  }

  public getOffices$() {
    if (this.#offices.value.length === 0) {
      return this.#candidateService.getOffices()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#offices.next(data); }))
    }
    return this.#offices.asObservable();
  }

  public getRelations$() {
    if (this.#getRelations.value.length === 0) {
      return this.#candidateService.getRelations()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#getRelations.next(data); }))
    }
    return this.#getRelations.asObservable();
  }

  public getSkills$() {
    if (this.#skills.value.length === 0) {
      return this.#candidateService.getSkills()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#skills.next(data); }))
    }
    return this.#skills.asObservable();
  }

  public getLegalDocTypes$() {
    if (this.#documentTypes.value.length === 0) {
      return this.#candidateService.getLegalDocTypes()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#documentTypes.next(data); }))
    }
    return this.#documentTypes.asObservable();
  }

  public getIssuePlaces$() {
    if (this.#issuePlaces.value.length === 0) {
      return this.#candidateService.getIssuePlaces()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#issuePlaces.next(data); }))
    }
    return this.#issuePlaces.asObservable();
  }

  public getCity$() {
    if (this.#city.value.length === 0) {
      return this.#candidateService.getCity()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#city.next(data); }))
    }
    return this.#city.asObservable();
  }

  public getEmployees$() {
    if (this.#employees.value.length === 0) {
      return this.#candidateService.getAllEmployees()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#employees.next(data); }))
    }
    return this.#employees.asObservable();
  }

  public getAttachmentTypes$() {
    if (this.#attachmentTypes.value.length === 0) {
      return this.#candidateService.getAttachmentTypes()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#attachmentTypes.next(data); }))
    }
    return this.#attachmentTypes.asObservable();
  }

  public getMarriedStatuses$() {
    if (this.#marriedStatuses.value.length === 0) {
      return this.#candidateService.getMarriedStatus()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#marriedStatuses.next(data); }))
    }
    return this.#marriedStatuses.asObservable();
  }

  public getSummaryType$() {
    if (this.#summaryType.value.length === 0) {
      return this.#candidateService.getSummaryType()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#summaryType.next(data); }))
    }
    return this.#summaryType.asObservable();
  }

  public getGenders$() {
    if (this.#genders.value.length === 0) {
      return this.#candidateService.getGenders()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#genders.next(data); }))
    }
    return this.#genders.asObservable();
  }

  public getEmployeeLevel$() {
    if (this.#employeeLevel.value.length === 0) {
      return this.#candidateService.getEmployeeLevel()
        .pipe(tap((data) => { UtilCommon.sortArray(data); this.#employeeLevel.next(data); }))
    }
    return this.#employeeLevel.asObservable();
  }
}